<template>
    <section :class="['chart-wrapper', customClass]">
        <ChartHeader
            :title="title"
            :subtitle="subtitle"
            :show-legend="ready && showLegend"
            :legend-items="legendItems"
            :series-data="seriesData"
            :hidden-series="hiddenSeries"
            @toggle-series="toggleSeries"
            :show-total-legend="showTotal" />
        <div class="chart-container">
            <ChartSkeleton :type="skeletonType" v-if="!ready" />
            <slot 
                v-else
                :series="visibleAnimatedSeries"
                :data="animatedData" 
            />
        </div>
    </section>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import ChartHeader from './ChartHeader.vue'
import ChartSkeleton from './ChartSkeleton.vue'

const props = defineProps({
    title: String,
    subtitle: String,
    legendItems: Array,
    hiddenSeries: Array,
    seriesData: Array,
    toggleSeries: Function,
    skeletonType: {
        type: String,
        required: true
    },
    ready: {
        type: Boolean,
        required: true
    },
    showLegend: {
        type: Boolean,
        default: true
    },
    customClass: {
        type: String,
        default: ''
    },
    showTotal: {
        type: Boolean,
        default: true
    },
    pieData: {
        type: Array,
        default: () => []
    }
})

const animatedSeriesData = ref([])
const animatedData = ref([])

const visibleAnimatedSeries = computed(() => {
    if (!props.hiddenSeries) return animatedSeriesData.value
    
    return animatedSeriesData.value.map((serie, index) => {
        if (props.hiddenSeries.has && props.hiddenSeries.has(index)) {
            return {
                ...serie,
                data: serie.data.map(() => null),
            }
        }
        return serie
    })
})

watch(() => props.ready, async (newReady, oldReady) => {
    if (newReady && !oldReady) {
        //reset data to empty for animation
        if (props.seriesData && props.seriesData.length > 0) {
            animatedSeriesData.value = props.seriesData.map(series => ({
                ...series,
                data: []
            }))
        }
        
        if (props.pieData && props.pieData.length > 0) {
            animatedData.value = []
        }
        
        await nextTick()
        setTimeout(() => {
            if (props.seriesData) {
                animatedSeriesData.value = props.seriesData
            }
            if (props.pieData) {
                animatedData.value = props.pieData
            }
        }, 50)
    } else if (newReady) {
        animatedSeriesData.value = props.seriesData || []
        animatedData.value = props.pieData || []
    }
}, { immediate: true })

// Watch for change then update
watch(() => props.seriesData, (newData) => {
    if (props.ready) {
        animatedSeriesData.value = newData || []
    }
}, { deep: true })

watch(() => props.pieData, (newData) => {
    if (props.ready) {
        animatedData.value = newData || []
    }
}, { deep: true })
</script>

<style scoped>
.chart-wrapper {
    background: var(--color-background);
    border: var(--border-card);
    border-radius: var(--border-radius-card);
    box-shadow: var(--shadow-card);
    padding: 24px;
    width: auto;
    height: 100%;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.chart-wrapper:hover {
    box-shadow: var(--shadow-card-hover);
    transform: translateY(-2px);
}

.chart-container {
    position: relative;
    min-height: 360px;
}
</style>